import { getMapBalance } from "./balance.js";

const MIN_INTERVAL = 85;

const ENEMY_COST = {
	scout: 1,
	soldier: 2.35,
	sprinter: 1.45,
	tank: 5.5,
	healer: 4.2,
	reinforced: 5.8,
	fireman: 4.1,
	frostbite: 4.7,
	boss: 16,
};

const PACKS = [
	{
		id: "scout-rush",
		minWave: 1,
		tags: ["swarm", "speed"],
		build: ({ budget, waveNumber, profile }) => [{
			type: "scout",
			count: Math.max(4, Math.round((budget / ENEMY_COST.scout) * (0.58 + profile.swarm * 0.12))),
			interval: Math.max(MIN_INTERVAL, Math.round(520 - waveNumber * 11 - profile.speed * 25)),
		}],
	},
	{
		id: "soldier-line",
		minWave: 2,
		tags: ["swarm", "armor"],
		build: ({ budget, waveNumber, profile }) => [{
			type: "soldier",
			count: Math.max(3, Math.round((budget / ENEMY_COST.soldier) * (0.94 + profile.pressure * 0.08))),
			interval: Math.max(MIN_INTERVAL, Math.round(760 - waveNumber * 9 - profile.pressure * 24)),
		}],
	},
	{
		id: "sprinter-burst",
		minWave: 4,
		tags: ["speed"],
		build: ({ budget, waveNumber, profile }) => [{
			type: "sprinter",
			count: Math.max(4, Math.round((budget / ENEMY_COST.sprinter) * (0.48 + profile.speed * 0.16))),
			interval: Math.max(MIN_INTERVAL, Math.round(420 - waveNumber * 8 - profile.speed * 34)),
		}],
	},
	{
		id: "tank-column",
		minWave: 5,
		tags: ["armor"],
		build: ({ budget, waveNumber, profile }) => [{
			type: "tank",
			count: Math.max(1, Math.round((budget / ENEMY_COST.tank) * (0.92 + profile.armor * 0.12))),
			interval: Math.max(240, Math.round(1350 - waveNumber * 12 - profile.armor * 45)),
		}],
	},
	{
		id: "fire-line",
		minWave: 6,
		tags: ["speed", "armor"],
		build: ({ budget, waveNumber, profile }) => [{
			type: "fireman",
			count: Math.max(2, Math.round((budget / ENEMY_COST.fireman) * (0.72 + profile.speed * 0.12))),
			interval: Math.max(180, Math.round(930 - waveNumber * 8 - profile.speed * 28)),
		}],
	},
	{
		id: "support-column",
		minWave: 8,
		tags: ["support"],
		build: ({ budget, waveNumber, profile }) => [
			{
				type: "soldier",
				count: Math.max(4, Math.round((budget / ENEMY_COST.soldier) * 0.4)),
				interval: Math.max(MIN_INTERVAL, Math.round(640 - waveNumber * 8)),
			},
			{
				type: "healer",
				count: Math.max(1, Math.round((budget / ENEMY_COST.healer) * (0.3 + profile.support * 0.08))),
				interval: Math.max(300, Math.round(1450 - waveNumber * 8 - profile.support * 40)),
			},
		],
	},
	{
		id: "frost-screen",
		minWave: 8,
		tags: ["support", "armor"],
		build: ({ budget, waveNumber, profile }) => [
			{
				type: "frostbite",
				count: Math.max(2, Math.round((budget / ENEMY_COST.frostbite) * (0.42 + profile.support * 0.1))),
				interval: Math.max(220, Math.round(980 - waveNumber * 8 - profile.support * 32)),
			},
			{
				type: "soldier",
				count: Math.max(2, Math.round((budget / ENEMY_COST.soldier) * 0.26)),
				interval: Math.max(MIN_INTERVAL, Math.round(620 - waveNumber * 7)),
			},
		],
	},
	{
		id: "reinforced-wall",
		minWave: 10,
		tags: ["armor"],
		build: ({ budget, waveNumber, profile }) => [{
			type: "reinforced",
			count: Math.max(1, Math.round((budget / ENEMY_COST.reinforced) * (0.66 + profile.armor * 0.12))),
			interval: Math.max(240, Math.round(1120 - waveNumber * 8 - profile.armor * 34)),
		}],
	},
	{
		id: "boss-escort",
		minWave: 10,
		bossOnly: true,
		tags: ["boss", "armor", "support"],
		build: ({ budget, waveNumber, profile }) => [
			{
				type: "boss",
				count: Math.max(1, Math.round((budget / ENEMY_COST.boss) * (0.22 + profile.boss * 0.04))),
				interval: Math.max(800, Math.round(1900 - waveNumber * 7 - profile.boss * 30)),
			},
			{
				type: profile.support > profile.armor ? "healer" : "reinforced",
				count: Math.max(1, Math.round((budget / ENEMY_COST.reinforced) * 0.28)),
				interval: Math.max(300, Math.round(1260 - waveNumber * 6)),
			},
		],
	},
	{
		id: "mixed-pressure",
		minWave: 7,
		tags: ["swarm", "support", "armor"],
		build: ({ budget, waveNumber }) => [
			{
				type: "soldier",
				count: Math.max(3, Math.round((budget / ENEMY_COST.soldier) * 0.36)),
				interval: Math.max(MIN_INTERVAL, Math.round(610 - waveNumber * 8)),
			},
			{
				type: "sprinter",
				count: Math.max(2, Math.round((budget / ENEMY_COST.sprinter) * 0.2)),
				interval: Math.max(MIN_INTERVAL, Math.round(340 - waveNumber * 6)),
			},
			{
				type: "tank",
				count: Math.max(1, Math.round((budget / ENEMY_COST.tank) * 0.18)),
				interval: Math.max(250, Math.round(1280 - waveNumber * 8)),
			},
		],
	},
];

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

const mulberry32 = (seed) => {
	let value = seed >>> 0;
	return () => {
		value += 0x6D2B79F5;
		let next = value;
		next = Math.imul(next ^ (next >>> 15), next | 1);
		next ^= next + Math.imul(next ^ (next >>> 7), next | 61);
		return ((next ^ (next >>> 14)) >>> 0) / 4294967296;
	};
};

const hashSeed = (...values) => {
	let hash = 2166136261;
	for (const value of values) {
		const text = String(value);
		for (let index = 0; index < text.length; index += 1) {
			hash ^= text.charCodeAt(index);
			hash = Math.imul(hash, 16777619);
		}
	}
	return hash >>> 0;
};

const weightedPick = (items, getWeight, random) => {
	const weighted = items
		.map((item) => ({ item, weight: Math.max(0, getWeight(item)) }))
		.filter((entry) => entry.weight > 0);
	if (!weighted.length) return null;

	const total = weighted.reduce((sum, entry) => sum + entry.weight, 0);
	let target = random() * total;
	for (const entry of weighted) {
		target -= entry.weight;
		if (target <= 0) return entry.item;
	}
	return weighted[weighted.length - 1].item;
};

const getProfile = ({ mapIdx = 0, mapDef = null } = {}) => getMapBalance({ mapIdx, mapDef });

const getWaveFocuses = (profile, waveNumber, runMode, random, adaptiveWeights = {}) => {
	const focusPool = [
		{ key: "swarm", weight: profile.swarm * (adaptiveWeights.swarm || 1) },
		{ key: "speed", weight: profile.speed * (adaptiveWeights.speed || 1) },
		{ key: "armor", weight: profile.armor * (adaptiveWeights.armor || 1) },
		{ key: "support", weight: profile.support * (adaptiveWeights.support || 1) },
		{ key: "boss", weight: profile.boss * (waveNumber >= 10 ? 1 : 0.18) * (adaptiveWeights.boss || 1) },
	];

	const focusCount = runMode === "survival"
		? clamp(2 + Math.floor(waveNumber / 12), 2, 4)
		: clamp(2 + Math.floor(waveNumber / 9), 2, 3);
	const selected = new Set();

	while (selected.size < focusCount) {
		const choice = weightedPick(focusPool, (entry) => selected.has(entry.key) ? 0 : entry.weight, random);
		if (!choice) break;
		selected.add(choice.key);
	}

	return selected;
};

const getWaveBudget = ({ runMode, waveNumber, profile }) => {
	if (runMode === "campaign") {
		const lateRamp = waveNumber >= 14 ? (waveNumber - 13) * 2.2 * profile.campaignLateBudget : 0;
		return (12 + waveNumber * 4.9 + profile.pressure * 4.4 + lateRamp) * profile.campaignBudget;
	}

	const tier = Math.floor(Math.max(0, waveNumber - 1) / 5);
	const earlyRelief = waveNumber <= 5 ? (6 - waveNumber) * 2.6 : 0;
	const lateRamp = waveNumber >= 9 ? (waveNumber - 8) * 1.05 : 0;
	return (13 + waveNumber * 4.7 + profile.pressure * 4.2 + tier * 4.1 + lateRamp - earlyRelief) * profile.survivalBudget;
};

const getBodyCap = ({ runMode, waveNumber, profile }) => {
	if (runMode === "campaign") {
		if (waveNumber <= 5) return profile.campaignBodyCap[0];
		if (waveNumber <= 10) return profile.campaignBodyCap[1];
		if (waveNumber <= 15) return profile.campaignBodyCap[2];
		return profile.campaignBodyCap[3];
	}
	const earlyOffset = waveNumber <= 4 ? -3 : waveNumber <= 8 ? -1 : 0;
	const lateOffset = waveNumber >= 14 ? Math.floor((waveNumber - 13) / 2) : 0;
	return profile.survivalBodyCapBase + waveNumber + earlyOffset + lateOffset;
};

const isBossWave = ({ runMode, waveNumber }) => {
	if (runMode === "campaign") return [10, 15, 20].includes(waveNumber);
	return waveNumber >= 8 && waveNumber % 5 === 0;
};

const scaleCountsToCap = (groups, bodyCap) => {
	const totalBodies = groups.reduce((sum, group) => sum + group.count, 0);
	if (totalBodies <= bodyCap) return groups;

	const ratio = bodyCap / totalBodies;
	return groups
		.map((group) => ({
			...group,
			count: Math.max(1, Math.round(group.count * ratio)),
		}))
		.filter((group) => group.count > 0);
};

const mergeGroups = (groups) => {
	const merged = new Map();
	for (const group of groups) {
		const existing = merged.get(group.type);
		if (!existing) {
			merged.set(group.type, { ...group });
			continue;
		}
		existing.count += group.count;
		existing.interval = Math.min(existing.interval, group.interval);
	}
	return [...merged.values()].sort((left, right) => left.interval - right.interval);
};

export const getWaveDefinition = ({
	runMode = "campaign",
	waveIndex = 0,
	mapIdx = 0,
	mapDef = null,
	runSeed = 1,
	adaptiveContext = null,
} = {}) => {
	const waveNumber = waveIndex + 1;
	const profile = getProfile({ mapIdx, mapDef });
	const random = mulberry32(hashSeed(runMode, mapIdx, waveNumber, runSeed));
	const bossWave = isBossWave({ runMode, waveNumber });
	const adaptiveWeights = adaptiveContext?.tagWeights || {};
	const focuses = getWaveFocuses(profile, waveNumber, runMode, random, adaptiveWeights);
	const bodyCap = Math.max(6, Math.round(getBodyCap({ runMode, waveNumber, profile }) * (adaptiveContext?.bodyCapMultiplier || 1)));
	const waveBudget = getWaveBudget({ runMode, waveNumber, profile }) * (adaptiveContext?.budgetMultiplier || 1);
	const packCount = runMode === "campaign"
		? clamp(2 + Math.floor(waveNumber / 6), 2, bossWave ? 4 : 3)
		: clamp(2 + Math.floor(waveNumber / 6), 2, bossWave ? 5 : 4);

	const groups = [];
	const selectedPackIds = new Set();
	let remainingBudget = waveBudget;

	for (let index = 0; index < packCount && remainingBudget > 2; index += 1) {
		const pack = weightedPick(PACKS, (candidate) => {
			if (candidate.minWave > waveNumber) return 0;
			if (candidate.bossOnly && !bossWave) return 0;
			if (!candidate.bossOnly && bossWave && candidate.tags.includes("boss")) return 0;

			let weight = selectedPackIds.has(candidate.id) ? 0.28 : 1;
			for (const tag of candidate.tags) {
				if (focuses.has(tag)) weight *= 1.38;
				if (tag === "swarm") weight *= profile.swarm;
				if (tag === "speed") weight *= profile.speed;
				if (tag === "armor") weight *= profile.armor;
				if (tag === "support") weight *= profile.support;
				if (tag === "boss") weight *= profile.boss;
				weight *= adaptiveWeights[tag] || 1;
			}
			if (adaptiveContext?.pressureBias > 0.12 && candidate.tags.includes("boss")) weight *= 1.08;
			if (adaptiveContext?.assessment?.gaps?.fire > 0.45 && candidate.tags.includes("speed")) weight *= 1.08;
			return weight;
		}, random);

		if (!pack) break;
		selectedPackIds.add(pack.id);

		const packBudget = Math.max(4, remainingBudget * (0.3 + random() * 0.16));
		const nextGroups = pack.build({
			budget: packBudget,
			waveNumber,
			mapIdx,
			mapDef,
			profile,
			runMode,
			random,
		});
		groups.push(...nextGroups);
		remainingBudget -= packBudget * 0.72;
	}

	if (bossWave && !groups.some((group) => group.type === "boss")) {
		groups.push({
			type: "boss",
			count: waveNumber >= 20 || runMode === "survival" ? 2 : 1,
			interval: Math.max(900, 1850 - waveNumber * 12),
		});
	}

	const capped = scaleCountsToCap(mergeGroups(groups), bodyCap);
	return capped.map((group) => ({
		...group,
		interval: Math.max(MIN_INTERVAL, Math.round(group.interval)),
	}));
};
